/**
 * Reading trades — the projected path and the full path, and the ledger that
 * keeps them apart.
 *
 * Two functions, and the difference between them is the whole point:
 *
 *   `loadTradeMetrics()` — every trade, 11 columns. Feeds `selectors/`. The rows
 *                          are NOT trades and can never be saved.
 *   `loadFullTrades(ids)` — the trades the user actually opened, all 48 columns.
 *                           Registers them in the ledger, which is what makes
 *                           them writable at all.
 *
 * A row read by the first and saved would destroy every column it did not carry.
 * That is not prevented here by care — `tradeToDb` refuses any trade whose id is
 * not in the ledger, and only `loadFullTrades` puts one there. See `hydration.ts`.
 */
import type { SupabaseClient } from '@supabase/supabase-js';
import type { MetricsRow } from '../../domain/projection';
import type { Trade } from '../../domain/types';
import type { HydrationLedger } from './hydration';
import { METRICS_SELECT, metricsRowFromDb } from './projection';
import { boundedReceipt, wholeBookReceipt, type ReadReceipt } from './receipt';
import { tradeFromDb, type DbRow } from './serialize';

/**
 * Rows per request.
 *
 * PostgREST caps a response server-side, so paging is not optional. 1,000 matches
 * the shipping client, which is a deliberate choice rather than a copied one: the
 * cap interacts with the server's own `max-rows` setting, and a larger page that
 * the server silently truncates would look like a short page and end the loop
 * early — losing trades with no error at all.
 */
export const PAGE_ROWS = 1_000;

export interface LoadResult<T> {
  rows: T[];
  /** How many requests it took. Surfaced because round trips are a real cost. */
  pages: number;
  /**
   * `{ id, updated_at }` per row, for `buildUpdatedAtMaps`.
   *
   * Returned SEPARATELY rather than folded into the rows because `updated_at` is
   * not a `Trade` field — it is concurrency metadata, and putting it on a
   * `MetricsRow` would invite domain code to read it.
   *
   * ⚠ Returned AT ALL because the alternative is silent. Feed the guard a
   * projection that never selected `updated_at` and `buildUpdatedAtMaps` yields
   * an empty Map, which `partitionByStaleness` reads as "fresh" for every row —
   * the two-tab guard disabled with nothing reporting it.
   */
  guardRows: { id: unknown; updated_at: unknown }[];
  /** What this read actually covered. Turned into a scope by `selectors/`. */
  receipt: ReadReceipt;
}

/**
 * How far back the boot read reaches.
 *
 * 45 days strictly contains the `today`, `yesterday`, `week` and `month` presets
 * on every day of every month — including the 1st, where a month-derived bound
 * would be a one-day window and an empty dashboard.
 *
 * The 2-day pad covers session attribution: a session date differs from its
 * calendar date by at most one day (the 18:00 ET rollover), and the read bounds
 * on `date` because that is the indexed column.
 */
export const BOOT_WINDOW_DAYS = 45;
export const SESSION_PAD_DAYS = 2;

/** The `date >=` bound for a boot read taken on `today` (an ISO day). */
export function bootWindowStart(today: string): string {
  const d = new Date(`${today}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() - (BOOT_WINDOW_DAYS + SESSION_PAD_DAYS));
  return d.toISOString().slice(0, 10);
}

/**
 * Every trade, projected.
 *
 * ⚠ ORDERED BY `id`, and the order is load-bearing rather than cosmetic.
 * `range()` paging over an UNORDERED query has no defined page boundary, so a
 * concurrent insert can shift rows between pages — silently dropping one and
 * duplicating another. The shipping client orders every paged read for the same
 * reason.
 */
export async function loadTradeMetrics(
  sb: SupabaseClient,
  opts: { since?: string; before?: string } = {},
): Promise<LoadResult<MetricsRow>> {
  const rows: MetricsRow[] = [];
  const guardRows: { id: unknown; updated_at: unknown }[] = [];
  let pages = 0;

  for (let from = 0; ; from += PAGE_ROWS) {
    // ⚠ PAGES TO EXHAUSTION INSIDE THE BOUND, not one page.
    //
    // A single `.range(0, 999)` looks sufficient for a 45-day window and is not:
    // a trader doing 25+ trades a day exceeds 1,000 inside it. Ordered by date
    // DESC that silently drops the OLDEST part of the window — the exact slice
    // the `month` preset claims to cover — and every KPI reads as a confident
    // number over a population nobody asked for.
    let q = sb.from('trades').select(METRICS_SELECT);
    if (opts.since) q = q.gte('date', opts.since);
    if (opts.before) q = q.lt('date', opts.before);
    const { data, error } = await q
      .order('id', { ascending: true })
      .range(from, from + PAGE_ROWS - 1);
    pages++;

    // Fail LOUD. A partial trade set produces a complete-looking set of wrong
    // numbers — a KPI panel reading $0 across a profitable account, internally
    // consistent, with nothing to indicate the read failed. Returning what
    // arrived would be the worst outcome available.
    if (error) {
      throw new Error(
        `loadTradeMetrics: page ${pages} (rows ${from}–${from + PAGE_ROWS - 1}) failed: ` +
          `${error.message}. Refusing to return a partial trade set — every metric would ` +
          `be silently wrong rather than visibly missing.`,
      );
    }
    if (!data || !data.length) break;

    for (const r of data) {
      const row = r as unknown as DbRow;
      rows.push(metricsRowFromDb(row));
      guardRows.push({ id: row['id'], updated_at: row['updated_at'] });
    }
    if (data.length < PAGE_ROWS) break; // short page = last page
  }

  // The loop only exits on an empty or short page — an error throws — so
  // reaching here means the rows inside the bound were exhausted.
  const receipt = opts.since
    ? boundedReceipt(opts.since, pages, true)
    : wholeBookReceipt(pages);
  return { rows, pages, guardRows, receipt };
}

/**
 * The other half of a bounded boot: everything strictly BEFORE the bound.
 *
 * Returned as its own result so the caller can merge the two receipts — which is
 * the only way a bounded read earns whole-book coverage and unblocks the
 * population-scoped numbers.
 */
export async function loadTradeMetricsBackfill(
  sb: SupabaseClient,
  before: string,
): Promise<LoadResult<MetricsRow>> {
  const r = await loadTradeMetrics(sb, { before });
  // An upper-bounded read is still unbounded BELOW, so it reaches all the way
  // back — which is what `mergeReceipts` requires of a completing backfill.
  return { ...r, receipt: wholeBookReceipt(r.pages) };
}

/**
 * Hydrate specific trades in full, and register them as writable.
 *
 * Call this before ANY edit — including a bulk edit, which must hydrate its whole
 * selection first. The ledger write is the only thing that makes these trades
 * saveable, so hydration and permission-to-save are the same act by construction
 * rather than by discipline.
 *
 * `select('*')` rather than an enumerated column list: a hand-written "full"
 * list drifts the moment a migration adds a column, and the row would then be
 * partial while looking complete. `assertFullTradeRow` inside `tradeFromDb`
 * catches that, but not drifting in the first place is better.
 */
export async function loadFullTrades(
  sb: SupabaseClient,
  ids: readonly string[],
  ledger: HydrationLedger,
): Promise<Trade[]> {
  if (!ids.length) return [];
  const out: Trade[] = [];

  // Chunked because a URL-encoded `in` list has a practical length limit; the
  // guard's FETCH_CHUNK uses 200 for the same reason.
  const CHUNK = 200;
  for (let i = 0; i < ids.length; i += CHUNK) {
    const slice = ids.slice(i, i + CHUNK);
    const { data, error } = await sb.from('trades').select('*').in('id', slice);
    if (error) {
      throw new Error(
        `loadFullTrades: hydrating ${slice.length} trade(s) failed: ${error.message}. ` +
          `None were registered, so none can be saved — which is the safe outcome: ` +
          `saving a trade that was never fully read destroys the columns it lacks.`,
      );
    }
    for (const r of data ?? []) {
      // tradeFromDb throws on a partial row, so a server that returned fewer
      // columns than `*` implies cannot reach the ledger.
      const t = tradeFromDb(r as DbRow);
      out.push(t);
      ledger.markHydrated(String(t.id));
    }
  }

  // A requested id that came back missing is NOT hydrated, and must not be
  // silently treated as though it were. Reported rather than thrown: the row may
  // legitimately have been deleted by another tab, and a hard failure there would
  // block a save the user can otherwise complete.
  const got = new Set(out.map((t) => String(t.id)));
  const missing = ids.filter((id) => !got.has(String(id)));
  if (missing.length) {
    console.warn(
      `loadFullTrades: ${missing.length} of ${ids.length} requested trade(s) were not ` +
        `returned and remain un-hydrated: ${missing.slice(0, 5).join(', ')}` +
        `${missing.length > 5 ? '…' : ''}. They cannot be saved until read successfully.`,
    );
  }

  return out;
}
